import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import WebpImage from "@src/component/base/WebpImage";
import useUser from "@src/hook/useUser";
import { GroupA } from "@src/ruix";
import { useRouter } from "next/router";
import { useState } from "react";
import { MarketingMenuBoardModal } from "./MarketingMenuBoardModal";
import { apiServiceV2 } from "@src/api";

const GnbButton = styled(Button)`
	padding: 4px 8px;
	height: auto;
	width: auto;
	min-width: fit-content;
	font-weight: bold;
	font-size: 12px;
	font-style: normal;
	font-weight: 700;
	line-height: 130%;
	white-space: nowrap;
	transition: all 0.2s ease-in-out;
	&:hover {
		transform: scale(1.05);
	}
`;

const HighlightButton = styled(GnbButton)`
	background-color: #fff !important;
	color: #000 !important;
	font-weight: 800 !important;
	transition: all 0.2s ease-in-out;
	display: flex;
	align-items: center;
	gap: 4px;

	.blue {
		color: #387cf6;
	}

	.beta {
		background-color: #387cf6;
		padding: 2px 4px;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 2px;

		span {
			font-weight: bold;
			color: white;
			font-size: 8px;
			line-height: 8px;
		}
	}

	&:hover {
		background-color: #f0f5ff !important;
		transform: scale(1.05);
	}
`;

const MenuBoardButton = styled(GnbButton)`
	background-color: #3D4148 !important;
	color: #fff !important;
	font-weight: 800 !important;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	margin-right: 8px;
	padding: 6px 12px;
	display: flex;
	align-items: center;
	gap: 6px;

	.new-badge {
		background-color: #387cf6;
		color: white;
		font-size: 8px;
		font-weight: 900;
		padding: 2px 4px;
		border-radius: 4px;
		line-height: 1;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&:hover {
		background-color: #4A4E57 !important;
		transform: scale(1.05);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
	}
`;

const MainGnbContainer = styled.div`
	width: 100%;
	min-height: 56px;
	padding: 26px 40px;
	position: sticky;
	background-color: var(--card-bg);
	gap: 10px;
	top: 0;
	z-index: 10000;

	@media (max-width: 1175px) {
		.gnb-nav {
			display: none;
		}
	}
`;

interface MainGnbProps {
	type: "client" | "expert" | "none";
	noBanner?: boolean;
}

export const MainGnb = (props: MainGnbProps) => {
	const { type, noBanner } = props;
	const { user } = useUser();

	const router = useRouter();

	const [menuBoardOpen, setMenuBoardOpen] = useState(false);

	const onClickLoginClient = () => {
		router.push("/signin?type=client");
	};

	const onClickLoginExpert = () => {
		router.push("/signin?type=expert");
	};

	const onClickDashboard = () => {
		router.push("/match/my");
	};

	const onClickMenuBoard = () => {
		if (user.isLogin) {
			apiServiceV2.post("/utils/click-menu-board").catch((err) => console.error(err));
			window.open(
				"https://shrouded-meal-521.notion.site/2aff8567bbfa8029809bc906e4277307?v=2aff8567bbfa80218222000cad0af51c",
				"_blank",
			);
		} else {
			setMenuBoardOpen(true);
		}
	};

	const getRightContent = () => {
		if (type === "client") {
			return (
				<>
					<GroupA order="row-center-between" gap={24}>
						<DashboardCtaButton onClick={onClickLoginClient}>
							로그인하기
						</DashboardCtaButton>
						<GnbMenuButton variant="text">
							<a href="/expert">
								전문가이신가요?
							</a>
						</GnbMenuButton>
					</GroupA>
				</>
			);
		} else if (type === "expert") {
			return (
				<>
					<GroupA order="row-center-between" gap={36}>
						<DashboardCtaButton onClick={onClickLoginExpert}>
							로그인하기
						</DashboardCtaButton>
						<GnbMenuButton variant="text">
							<a href="/">
								고객사이신가요?
							</a>
						</GnbMenuButton>
					</GroupA>
				</>
			);
		}
		return null;
	};

	const highlightButton = (
		<HighlightButton
			variant="contained"
			onClick={() =>
				window.open(
					"https://notion.1point.kr/agency-matching",
					"_blank",
				)
			}
		>
			탑티어 <span className="blue">대행사</span> 매칭받기
			<span className="beta">
				<span>BETA</span>
			</span>
		</HighlightButton>
	);

	const menuBoardButton = (
		<MenuBoardButton variant="contained" onClick={onClickMenuBoard}>
			마케팅 메뉴판 확인하기
			<span className="new-badge">NEW</span>
		</MenuBoardButton>
	);

	return (
		<>
			<MainGnbContainer className="column-center-center">
				<div
					className="row-center-between"
					style={{
						width: "100%",
						maxWidth: "1140px",
					}}
				>
					<GroupA order="row-center-between" gap={10}>
						<a href="/">
							<WebpImage
								style={{
									width: "130px",
									marginRight: "32px",
								}}
								src="/onepoint/assets/images/logo/h-logo.png"
							/>
						</a>
						<div className="gnb-nav">
						<GroupA order="row-center-between" gap={24}>
							<GnbMenuButton variant="text">
								<a
									href="https://featpaper.com/l/JemyuEY9"
									target="_blank"
									rel="noreferrer"
								>
									서비스 소개
								</a>
							</GnbMenuButton>
							<GnbMenuButton variant="text">
								<a
									className="hiring"
									href="https://1point.kr/blog"
								>
									블로그
								</a>
							</GnbMenuButton>
							<GnbMenuButton variant="text">
								<a
									className="hiring"
									href="https://1point.kr/blog/best-practices/"
								>
									성공 사례
								</a>
							</GnbMenuButton>
							<GnbMenuButton variant="text">
								<a
									className="hiring"
									href="/about"
								>
									회사 소개
								</a>
							</GnbMenuButton>
							{type === "client" && (
								<GroupA order="row-center-between" gap={4}>
									{menuBoardButton}
									{highlightButton}
								</GroupA>
							)}
						</GroupA>
						</div>
					</GroupA>
					<GroupA order="row-center-between">
						{user.isLogin ? (
							<>
								<DashboardCtaButton onClick={onClickDashboard}>
									대시보드 →
								</DashboardCtaButton>
							</>
						) : (
							getRightContent()
						)}
					</GroupA>
				</div>
			</MainGnbContainer>
			<MarketingMenuBoardModal open={menuBoardOpen} onClose={() => setMenuBoardOpen(false)} />
			{type === "client" && !noBanner ? (
				<Banner
					className="row-center-between"
					id="banner-close"
					onClick={() => {
						window.open(
							"https://docs.channel.io/onepoint/ko/articles/1286c10a-%EB%A6%AC%EC%8A%A4%ED%81%AC%ED%94%84%EB%A6%AC-%EC%A0%95%EC%B1%85-%EC%95%88%EB%82%B4",
						);
					}}
				>
					<div></div>
					<div className="text">
						🎉리스크프리 프로그램!{" "}
						<span style={{ fontWeight: 800 }}>전문가 불만족시 100% 환불!</span>✨
					</div>
					<CloseIcon
						style={{
							fill: "white",
							cursor: "pointer",
							width: "20px",
							height: "20px",
						}}
						onClick={(e) => {
							e.stopPropagation();
							document.getElementById("banner-close")!.style.display = "none";
						}}
					/>
				</Banner>
			) : (
				<></>
			)}
		</>
	);
};

const Banner = styled.div`
	width: 100%;
	background-color: var(--primary);
	padding: 8px;
	position: sticky;
	top: 82px;
	z-index: 10000;
	cursor: pointer;

	.text {
		color: white;
		font-size: 16px;
		font-weight: 500;
	}
`;

const GnbLogo = styled.img`
	width: 110px;
	margin-right: 32px;
`;

const GnbMenuButton = styled(Button)`
	min-width: fit-content;
	width: fit-content;
	padding: 0;
	color: #fff;
	text-align: center;
	font-family: Pretendard;
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
	line-height: 130%; /* 18.2px */
	letter-spacing: -0.42px;
	position: relative;

	.beta {
		background-color: var(--primary);
		padding: 3px 8px;
		border-radius: 20px;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		margin-left: 5px;
		margin-right: 0px;

		div {
			display: flex;
			align-items: center;
			justify-content: center;
			font-weight: bold;
			color: white;
			font-size: 10px;
			line-height: 10px;
			text-align: center;
			text-transform: none;
		}
	}
`;

const pulseGlow = keyframes`
	0%, 100% {
		box-shadow: 0 3px 12px rgba(56, 124, 246, 0.35);
	}
	50% {
		box-shadow: 0 0 0 6px rgba(56, 124, 246, 0.15), 0 3px 16px rgba(56, 124, 246, 0.55);
	}
`;

const DashboardCtaButton = styled.button`
	-webkit-appearance: none;
	appearance: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background-color: var(--primary);
	color: #fff;
	border: none;
	font-size: 16px;
	font-weight: 800;
	padding: 8px 22px;
	border-radius: 100px;
	cursor: pointer;
	white-space: nowrap;
	font-family: Pretendard, sans-serif;
	letter-spacing: -0.3px;
	line-height: 1.3;
	animation: ${pulseGlow} 2.5s ease-in-out infinite;
	transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out;

	&:hover {
		animation: none;
		background-color: #2563eb;
		transform: scale(1.04);
		box-shadow: 0 5px 18px rgba(56, 124, 246, 0.5);
	}

	&:active {
		transform: scale(0.98);
	}
`;
